import React, { useRef, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useViewport } from '@atlassian/jira-software-deployments-common-table/src/components/table/common/context/viewport/context';
import { LoadingStateOptions } from '@atlassian/jira-software-deployments-service-query-types';
import { useCoreData } from '../../../services/core-data';
import messages from './messages';

type Props = {
	threshold?: number;
};

const IssuePageFetcher = ({ threshold = 0 }: Props) => {
	const {
		serviceResult: { loadingState, data },
		getNextIssuePage,
		hasReachedMaxIssuesLimit,
	} = useCoreData();
	const { listenToViewportScroll, stopListeningToViewportScroll } = useViewport();

	const lastScrollHeightBeforePaginated = useRef<unknown>(null);
	const lastScrollTop = useRef<unknown>(null);
	const handleScroll = useCallback(
		(event: Event) => {
			if (event.target instanceof Element) {
				const { target } = event;

				const isDownwardVerticallyScrolled =
					lastScrollTop.current === null
						? false
						: // @ts-expect-error - TS2571 - Object is of type 'unknown'.
							lastScrollTop.current < target.scrollTop;
				lastScrollTop.current = target.scrollTop;

				if (lastScrollHeightBeforePaginated.current !== null) {
					// it appears that you can keep scrolling a container past its max height.
					// In this example if you scroll fast to the bottom and keep scrolling
					// there is a big jump when the new deployments load in.
					// TODO this isn't perfect at all, we need to fix this.
					// Maybe setting overscroll-behavior: none will help?
					// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'number'.
					target.scrollTop = lastScrollHeightBeforePaginated.current;
					lastScrollHeightBeforePaginated.current = null;
					return;
				}

				const isVerticalEndReached =
					target.scrollTop + target.clientHeight + threshold >= target.scrollHeight &&
					isDownwardVerticallyScrolled;

				if (
					isVerticalEndReached &&
					getNextIssuePage !== null &&
					loadingState === LoadingStateOptions.READY
				) {
					// load more issues and deployments but remember the scrollHeight so we don't
					// exceed this in the next animation frame.
					lastScrollHeightBeforePaginated.current = target.scrollHeight;
					getNextIssuePage();
				}
			}
		},
		[getNextIssuePage, loadingState, threshold],
	);

	useEffect(() => {
		listenToViewportScroll(handleScroll);
		return () => {
			stopListeningToViewportScroll(handleScroll);
		};
	}, [handleScroll, listenToViewportScroll, stopListeningToViewportScroll]);

	const { formatMessage } = useIntl();

	const issuesCount = data?.issues.length ?? 0;
	const isLoading = loadingState === LoadingStateOptions.LOADING_NEXT_PAGE;

	const message = hasReachedMaxIssuesLimit
		? formatMessage(messages.maxIssues)
		: formatMessage(messages.allIssues, { issuesCount });

	return (
		<ContentContainer>
			{isLoading && <Spinner size="small" />}
			{!isLoading && getNextIssuePage === null && <span>{message}</span>}
		</ContentContainer>
	);
};

IssuePageFetcher.defaultProps = {
	threshold: 150, // preload next deployments 150px before the bottom
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	padding: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
	textAlign: 'center',
});

export default IssuePageFetcher;
